import { validateHost, validatePort } from '../printing/printersApi';

/**
 * What a manager may change about their own restaurant, and what a saved
 * request is allowed to contain.
 *
 * ── Why this is a file of its own ────────────────────────────────────────
 *
 * The business record holds things the restaurant must not set: `fee` is what
 * Dalabtire charges them, `nextBillDate` is when it falls due, `notify` is
 * what puts the payment banner on their screen, `status` can disable the
 * account, `publicBusinessId` is baked into every printed QR code. The API
 * refuses those fields itself — but a form that spread its own state into the
 * request body would still be one rename away from trying, and the server is
 * not the only thing that should know the rule.
 *
 * So the payload is built here, by hand, one named key at a time. It is not a
 * spread and never will be: `normalise` cannot emit a key that is not written
 * out below, whatever it is handed.
 *
 * ── And why only the changed fields go ───────────────────────────────────
 *
 * The API says "send only the fields you want to change". Taking it at its
 * word matters for more than tidiness: a restaurant that has never set a logo
 * size has no `logoWidth`, and a form that sent every field every time would
 * invent one for them on the first save of an unrelated switch. `buildPayload`
 * diffs against what the server last told us and sends that.
 */

export const EDITABLE_FIELDS = [
  'businessName',
  'businessNumber',
  'businessLocation',
  'businessCategory',
  'logoUrl',
  'logoWidth',
  'logoHeight',
  'currencyMark',
  'paytoAccounts',
  'vatPercentage',
  'autoVat',
  'autoPrintReceipt',
  'receiptPrinterAddress',
  'stockUnits',
  'smsMessage',
  'smsCustomMessages',
  'publicMenu',
];

/**
 * Everything the API keeps for the superadmin, listed so a test can prove none
 * of them can leave this screen.
 */
export const FORBIDDEN_FIELDS = [
  'fee',
  'tier',
  'status',
  'nextBillDate',
  'notify',
  'smsAmount',
  'smsExpireDate',
  'smsName',
  'smsSuffix',
  'publicBusinessId',
  '_id',
  'id',
  'created_at',
  'updated_at',
];

export const MAX_NAME = 80;
export const MAX_SHORT = 60;
export const MAX_MARK = 4;
export const MAX_ACCOUNTS = 12;
export const MAX_UNITS = 30;
export const MAX_UNIT_LENGTH = 20;
export const MAX_SMS = 300;
export const MAX_SMS_CUSTOM = 12;

/** The size a logo is given the first time someone sets one. */
export const DEFAULT_LOGO_SIZE = 120;
export const MIN_LOGO_SIZE = 24;
export const MAX_LOGO_SIZE = 600;

/* ── little conversions ─────────────────────────────────────────────────── */

const text = (value) => String(value == null ? '' : value).trim();

/** A number field is held as a string while it is being typed. */
function num(value, fallback) {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

let nextKey = 1;
const key = (prefix) => {
  nextKey += 1;
  return `${prefix}-${nextKey}`;
};

export const blankAccount = () => ({ key: key('acct'), label: '', account: '' });
export const blankMessage = () => ({ key: key('sms'), name: '', message: '' });
export const blankUnit = () => ({ key: key('unit'), value: '' });

/* ── the business, as a form ────────────────────────────────────────────── */

export function formFromBusiness(business) {
  const accounts = Array.isArray(business?.paytoAccounts) ? business.paytoAccounts : [];
  const units = Array.isArray(business?.stockUnits) ? business.stockUnits : [];
  const custom = Array.isArray(business?.smsCustomMessages) ? business.smsCustomMessages : [];

  const logoUrl = String(business?.logoUrl || '');

  return {
    businessName: String(business?.businessName || ''),
    businessNumber: String(business?.businessNumber || ''),
    businessLocation: String(business?.businessLocation || ''),
    businessCategory: String(business?.businessCategory || ''),

    logoUrl,
    // Held as strings because they are typed into. A restaurant that has a
    // logo but no stored size gets the default, so the boxes are never blank
    // next to a picture that is plainly some size already.
    logoWidth: String(business?.logoWidth || (logoUrl ? DEFAULT_LOGO_SIZE : '')),
    logoHeight: String(business?.logoHeight || (logoUrl ? DEFAULT_LOGO_SIZE : '')),

    currencyMark: String(business?.currencyMark || '$'),

    paytoAccounts: accounts.map((row, index) => ({
      key: `acct-seed-${index}`,
      label: String(row?.label || ''),
      account: String(row?.account || ''),
    })),

    vatPercentage: String(business?.vatPercentage == null ? 0 : business.vatPercentage),
    autoVat: business?.autoVat === true,

    autoPrintReceipt: business?.autoPrintReceipt === true,
    receiptPrinterAddress: String(business?.receiptPrinterAddress || ''),

    stockUnits: units.map((value, index) => ({
      key: `unit-seed-${index}`,
      value: String(value || ''),
    })),

    smsMessage: String(business?.smsMessage || ''),
    smsCustomMessages: custom.map((row, index) => ({
      key: `sms-seed-${index}`,
      name: String(row?.name || ''),
      message: String(row?.message || ''),
    })),

    publicMenu: business?.publicMenu === true,
  };
}

/* ── the lists, as the server should store them ─────────────────────────── */

/**
 * A row is dropped only when it is entirely empty — someone who added a row
 * and thought better of it. A half-filled row is kept here and caught by
 * `validate` instead: silently dropping it would mean typing a label, pressing
 * Save, and watching the work disappear with no explanation.
 */
export function cleanAccounts(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({ label: text(row?.label), account: text(row?.account) }))
    .filter((row) => row.label || row.account);
}

export function cleanMessages(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({ name: text(row?.name), message: text(row?.message) }))
    .filter((row) => row.name || row.message);
}

/** Stock units are plain strings on the record, not objects. */
export function cleanUnits(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => text(row && row.value !== undefined ? row.value : row)).filter(Boolean);
}

/* ── the whitelist ──────────────────────────────────────────────────────── */

/**
 * Every editable field, in the shape the API stores it.
 *
 * Written out one key at a time on purpose — see the note at the top of this
 * file. Do not turn this into a spread or a loop.
 */
export function normalise(form) {
  const logoUrl = text(form?.logoUrl);

  return {
    businessName: text(form?.businessName),
    businessNumber: text(form?.businessNumber),
    businessLocation: text(form?.businessLocation),
    businessCategory: text(form?.businessCategory),

    logoUrl,
    logoWidth: num(form?.logoWidth, logoUrl ? DEFAULT_LOGO_SIZE : 0),
    logoHeight: num(form?.logoHeight, logoUrl ? DEFAULT_LOGO_SIZE : 0),

    currencyMark: text(form?.currencyMark),
    paytoAccounts: cleanAccounts(form?.paytoAccounts),

    vatPercentage: num(form?.vatPercentage, 0),
    autoVat: form?.autoVat === true,

    autoPrintReceipt: form?.autoPrintReceipt === true,
    receiptPrinterAddress: text(form?.receiptPrinterAddress),

    stockUnits: cleanUnits(form?.stockUnits),

    smsMessage: text(form?.smsMessage),
    smsCustomMessages: cleanMessages(form?.smsCustomMessages),

    publicMenu: form?.publicMenu === true,
  };
}

/**
 * Only what actually changed.
 *
 * With no `seed` it returns the lot, which is what the tests use to check the
 * whitelist itself. In the panel there is always a seed.
 */
export function buildPayload(form, seed) {
  const next = normalise(form);
  if (!seed) return next;

  const before = normalise(seed);
  const payload = {};

  EDITABLE_FIELDS.forEach((field) => {
    if (JSON.stringify(next[field]) !== JSON.stringify(before[field])) {
      payload[field] = next[field];
    }
  });

  return payload;
}

/* ── is it allowed to be saved ──────────────────────────────────────────── */

/**
 * @returns an object of messages, plus `ok`. Per-row problems are keyed by
 *          index under `accounts`, `units` and `messages`.
 */
export function validate(form) {
  const errors = { accounts: {}, units: {}, messages: {} };

  const name = text(form?.businessName);
  if (!name) errors.businessName = 'Enter the restaurant name';
  else if (name.length > MAX_NAME) errors.businessName = `Keep it under ${MAX_NAME} letters`;

  if (text(form?.businessNumber).length > MAX_SHORT)
    errors.businessNumber = `Keep it under ${MAX_SHORT} characters`;
  if (text(form?.businessLocation).length > MAX_NAME)
    errors.businessLocation = `Keep it under ${MAX_NAME} letters`;
  if (text(form?.businessCategory).length > MAX_SHORT)
    errors.businessCategory = `Keep it under ${MAX_SHORT} letters`;

  const mark = text(form?.currencyMark);
  if (!mark) errors.currencyMark = 'Enter a currency mark';
  else if (mark.length > MAX_MARK) errors.currencyMark = `At most ${MAX_MARK} characters`;
  else if (/\s/.test(mark)) errors.currencyMark = 'No spaces — it sits against the number';

  /* the logo, and only when there is one */
  if (text(form?.logoUrl)) {
    ['logoWidth', 'logoHeight'].forEach((field) => {
      const raw = form?.[field];
      const value = num(raw, NaN);
      const label = field === 'logoWidth' ? 'Width' : 'Height';
      if (raw === '' || raw === null || raw === undefined || !Number.isFinite(value))
        errors[field] = `${label} is needed`;
      else if (!Number.isInteger(value)) errors[field] = `${label} must be a whole number`;
      else if (value < MIN_LOGO_SIZE || value > MAX_LOGO_SIZE)
        errors[field] = `${label} must be between ${MIN_LOGO_SIZE} and ${MAX_LOGO_SIZE}`;
    });
  }

  /* VAT */
  const vatRaw = form?.vatPercentage;
  const vat = num(vatRaw, NaN);
  if (vatRaw === '' || vatRaw === null || vatRaw === undefined) errors.vatPercentage = 'Enter 0 if there is no VAT';
  else if (!Number.isFinite(vat)) errors.vatPercentage = 'That is not a number';
  else if (vat < 0) errors.vatPercentage = 'VAT cannot be negative';
  else if (vat > 100) errors.vatPercentage = 'VAT cannot be over 100%';

  /* the network receipt printer, if there is one */
  const address = text(form?.receiptPrinterAddress);
  if (address) {
    const bits = address.split(':');
    if (bits.length !== 2 || !validateHost(bits[0]) || !validatePort(bits[1]))
      errors.receiptPrinterAddress = 'Use the form 192.168.18.70:9100, or leave it empty';
  }

  /* payment accounts */
  const accounts = Array.isArray(form?.paytoAccounts) ? form.paytoAccounts : [];
  if (accounts.length > MAX_ACCOUNTS) errors.accounts.all = `At most ${MAX_ACCOUNTS} accounts`;

  const seenAccount = {};
  accounts.forEach((row, index) => {
    const label = text(row?.label);
    const account = text(row?.account);
    if (!label && !account) return;

    if (!label) {
      errors.accounts[index] = 'Give this account a name';
      return;
    }
    if (!account) {
      errors.accounts[index] = 'Enter the number for this account';
      return;
    }
    // The cashier picks an account by its name and the receipt prints that
    // name, so two accounts called EVC are two buttons nobody can tell apart.
    const k = label.toLowerCase();
    if (seenAccount[k]) errors.accounts[index] = `There is already an account called ${label}`;
    seenAccount[k] = true;
  });

  /* stock units */
  const units = Array.isArray(form?.stockUnits) ? form.stockUnits : [];
  if (units.length > MAX_UNITS) errors.units.all = `At most ${MAX_UNITS} units`;

  const seenUnit = {};
  units.forEach((row, index) => {
    const value = text(row?.value);
    if (!value) return;
    if (value.length > MAX_UNIT_LENGTH) {
      errors.units[index] = `Keep a unit under ${MAX_UNIT_LENGTH} characters`;
      return;
    }
    const k = value.toLowerCase();
    if (seenUnit[k]) errors.units[index] = `${value} is already in the list`;
    seenUnit[k] = true;
  });

  /* SMS */
  if (text(form?.smsMessage).length > MAX_SMS) errors.smsMessage = `Keep it under ${MAX_SMS} characters`;

  const messages = Array.isArray(form?.smsCustomMessages) ? form.smsCustomMessages : [];
  if (messages.length > MAX_SMS_CUSTOM) errors.messages.all = `At most ${MAX_SMS_CUSTOM} messages`;

  const seenMessage = {};
  messages.forEach((row, index) => {
    const name = text(row?.name);
    const message = text(row?.message);
    if (!name && !message) return;

    if (!name) {
      errors.messages[index] = 'Give this message a name';
      return;
    }
    if (!message) {
      errors.messages[index] = 'Write the message';
      return;
    }
    if (message.length > MAX_SMS) {
      errors.messages[index] = `Keep it under ${MAX_SMS} characters`;
      return;
    }
    const k = name.toLowerCase();
    if (seenMessage[k]) errors.messages[index] = `There is already a message called ${name}`;
    seenMessage[k] = true;
  });

  errors.ok =
    !errors.businessName &&
    !errors.businessNumber &&
    !errors.businessLocation &&
    !errors.businessCategory &&
    !errors.currencyMark &&
    !errors.logoWidth &&
    !errors.logoHeight &&
    !errors.vatPercentage &&
    !errors.receiptPrinterAddress &&
    !errors.smsMessage &&
    Object.keys(errors.accounts).length === 0 &&
    Object.keys(errors.units).length === 0 &&
    Object.keys(errors.messages).length === 0;

  return errors;
}

/* ── has anything actually changed ──────────────────────────────────────── */

/** The editable part of a business, as one comparable string. */
export function signatureOf(business) {
  return JSON.stringify(normalise(formFromBusiness(business)));
}

/** Two forms, compared the way the server will see them. */
export function sameForm(a, b) {
  return JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));
}

/**
 * The one change worth a warning before the save rather than after it: the
 * public menu going dark.
 */
export function willCloseMenu(form, seed) {
  return seed?.publicMenu === true && form?.publicMenu !== true;
}
